/**
 * Billing data access.
 *
 * Reads subscription state and starts checkout. It cannot *grant* anything —
 * `subscriptions` has no insert or update policy, so the only writer is the
 * Stripe webhook. That is deliberate: entitlement arrives signed by Stripe or
 * it does not arrive.
 *
 * Fails soft throughout. A billing outage must never stop a lesson, so every
 * failure here degrades to the free tier rather than to an error screen.
 */

import type { BillingInterval } from '@yuan-math/config';
import type { Subscription, Tier } from '@yuan-math/learning';
import { supabaseClient } from './auth.ts';

type SubscriptionRow = {
  id: string;
  tier: Tier;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  seats: number;
};

/**
 * The account's current subscription, or undefined.
 *
 * Undefined means free — an absent subscription is the normal state, not an
 * error, and most users will never have one.
 */
export async function fetchSubscription(accountId: string): Promise<Subscription | undefined> {
  const client = supabaseClient();
  if (!client) return undefined;

  try {
    const { data, error } = await client
      .from('subscriptions')
      .select('id, tier, status, current_period_end, cancel_at_period_end, seats')
      .eq('account_id', accountId)
      // A returning subscriber can have an old cancelled row alongside a new
      // one; the most recently updated is the live one.
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return undefined;
    const row = data as SubscriptionRow;

    return {
      tier: row.tier,
      status: row.status as Subscription['status'],
      ...(row.current_period_end ? { currentPeriodEnd: Date.parse(row.current_period_end) } : {}),
      cancelAtPeriodEnd: row.cancel_at_period_end,
      seats: row.seats,
    };
  } catch {
    return undefined;
  }
}

export type CheckoutResult = { url: string } | { error: string };

/**
 * Starts Stripe Checkout.
 *
 * The tier and the interval are sent, never a price. The Edge Function looks
 * the amount up from its own configuration — if it came from here, a
 * determined user could buy a year of Premium for a penny.
 */
export async function startCheckout(tier: Tier, interval: BillingInterval = 'month'): Promise<CheckoutResult> {
  const client = supabaseClient();
  if (!client) return { error: 'Payments are not available on this deployment.' };

  try {
    const { data, error } = await client.functions.invoke('create-checkout-session', {
      body: { tier, interval },
    });

    if (error) {
      // The function returns a readable sentence for the cases a user can act
      // on — a child account, an unconfigured plan.
      return { error: await messageFor(error, 'Could not start checkout just now.') };
    }

    const url = (data as { url?: string } | null)?.url;
    return url ? { url } : { error: 'Checkout did not return a payment page.' };
  } catch {
    return { error: UNREACHABLE };
  }
}

const UNREACHABLE = 'Could not reach the payment service.';

/**
 * What to tell the user about a failed invocation.
 *
 * Supabase wraps a non-2xx response, and the sentence the function wrote is
 * inside it. When there is no response at all the request never left the
 * browser — a refused CORS preflight, an offline laptop — and saying "could not
 * start checkout" there points at Stripe, which is not where the fault is. The
 * two cases looked identical from the page once, and the wrong half of the
 * stack was searched for a day because of it.
 */
async function messageFor(error: unknown, fallback: string): Promise<string> {
  const context = (error as { context?: Response }).context;
  if (!context || typeof context.json !== 'function') return UNREACHABLE;
  try {
    const body = (await context.json()) as { error?: string };
    return body.error ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Opens Stripe's billing portal, where a subscriber changes their card,
 * downloads invoices or cancels.
 *
 * Delegated rather than rebuilt: reimplementing cancellation and invoice
 * history is a great deal of work to arrive somewhere worse, and it would put
 * card details back in scope.
 */
export async function openBillingPortal(): Promise<CheckoutResult> {
  const client = supabaseClient();
  if (!client) return { error: 'Payments are not available on this deployment.' };

  try {
    const { data, error } = await client.functions.invoke('create-billing-portal');
    if (error) return { error: await messageFor(error, 'Could not open billing just now.') };
    const url = (data as { url?: string } | null)?.url;
    return url ? { url } : { error: 'Billing portal did not return a URL.' };
  } catch {
    return { error: UNREACHABLE };
  }
}
